/**
 * Application routes.
 *
 * Three route trees:
 *   - AuthLayout: /login, /forgot-password, /reset-password (no auth required)
 *   - PublicLayout: /apply (no auth, no shell - pilot intake wizard)
 *   - AppLayout: everything else (gated by RequireAuth)
 *
 * Routes wired to real backend endpoints:
 *   - /                                Dashboard with real KPIs from /api/v1/people/*
 *   - /people                          List + profile from /api/v1/people/persons
 *   - /control                         Cross-tenant cohort view (TNN admins)
 *   - /control/applications            Applications pipeline (TNN admins)
 *   - /control/applications/:id        Application detail / decide / provision
 *   - /apply                           Public pilot application wizard
 *   - /login, /forgot-password, /reset-password
 *
 * Code-splitting strategy:
 *
 *   Every page component is `lazy()`-imported. Vite + Rollup split each
 *   into its own chunk so the initial bundle only contains the router,
 *   shared layouts, and the page the user actually lands on. The first
 *   route load (post-login) fetches its own chunk on demand.
 *
 *   The wizard lives outside any layout - it's a chat-style page that
 *   manages its own viewport and shouldn't carry the app's chrome.
 *
 *   Login is also lazy. The pre-auth tree only renders that one page,
 *   so we don't lose anything by deferring it. The trade-off: a brief
 *   suspense fallback on first paint, replaced by the page itself.
 *
 *   Auth pages share a small bundle slice because they all use the
 *   same AuthLayout shell. Vite collapses common deps automatically.
 */
import type { ReactElement } from "react";
import { Suspense, lazy } from "react";

import { Button, Result } from "antd";
import { Navigate, Route, Routes, Link, useLocation, useParams } from "react-router-dom";


import { decideControlAccess } from "@/app/router/controlAccess";

import { useCurrentUser, useIsAuthenticated } from "@/features/auth/model/useAuth";
import { homePathForRole, useRoleAccess } from "@/features/auth/model/useRoleAccess";
import { SessionGuard } from "@/features/auth/ui/SessionGuard";
import { stashReturnPath } from "@/features/auth/lib/sessionReturnPath";
import { QA_PENDING_LOGIN_KEY } from "@/features/auth/qa/qaPendingLogin";
import { useDeployment } from "@/features/deployment/model/useDeployment";

import { AppLayout } from "@/shared/layouts/AppLayout";
import { AuthLayout } from "@/shared/layouts/AuthLayout";
import { ModuleGuard } from "@/shared/ui/ModuleGuard";
import { SettingsPageGuard } from "@/features/settings/ui/SettingsPageGuard";
import { PermissionGuard } from "@/shared/ui/PermissionGuard";
import { RoleGuard } from "@/shared/ui/RoleGuard";

// Lazy-loaded page components. Each becomes its own JS chunk on build.
// The named exports are wrapped in default-export adapters because
// React.lazy requires the imported module to have a default export.
const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import("@/pages/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })),
);
const ResetPasswordPage = lazy(() =>
  import("@/pages/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })),
);
const AcceptInvitePage = lazy(() =>
  import("@/pages/AcceptInvitePage").then((m) => ({ default: m.AcceptInvitePage })),
);
const TeamInvitationsPage = lazy(() =>
  import("@/pages/TeamInvitationsPage").then((m) => ({
    default: m.TeamInvitationsPage,
  })),
);
const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const CommunityPage = lazy(() =>
  import("@/pages/CommunityPage").then((m) => ({ default: m.CommunityPage })),
);
const DonorRecordPage = lazy(() =>
  import("@/pages/DonorRecordPage").then((m) => ({ default: m.DonorRecordPage })),
);
const DonorNotesPage = lazy(() =>
  import("@/pages/DonorNotesPage").then((m) => ({ default: m.DonorNotesPage })),
);
const DonorsPage = lazy(() =>
  import("@/pages/DonorsPage").then((m) => ({ default: m.DonorsPage })),
);
const PersonProfilePage = lazy(() =>
  import("@/pages/PersonProfilePage").then((m) => ({ default: m.PersonProfilePage })),
);
const PersonWorkTimelinePage = lazy(() =>
  import("@/pages/PersonWorkTimelinePage").then((m) => ({
    default: m.PersonWorkTimelinePage,
  })),
);
const ControlPlanePage = lazy(() =>
  import("@/pages/ControlPlanePage").then((m) => ({ default: m.ControlPlanePage })),
);
const KPIDashboardPage = lazy(() =>
  import("@/pages/KPIDashboardPage").then((m) => ({ default: m.KPIDashboardPage })),
);
const FundDetailPage = lazy(() =>
  import("@/pages/FundDetailPage").then((m) => ({ default: m.default })),
);
const AccountingPage = lazy(() =>
  import("@/pages/AccountingPage").then((m) => ({ default: m.AccountingPage })),
);
const GrantManagementPage = lazy(() =>
  import("@/pages/GrantManagementPage").then((m) => ({ default: m.GrantManagementPage })),
);
const SubmissionCenterPage = lazy(() =>
  import("@/pages/SubmissionCenterPage").then((m) => ({ default: m.SubmissionCenterPage })),
);
const SchedulingPage = lazy(() =>
  import("@/pages/SchedulingPage").then((m) => ({ default: m.SchedulingPage })),
);
const EventsPage = lazy(() =>
  import("@/pages/EventsPage").then((m) => ({ default: m.EventsPage })),
);
const GrantsPage = lazy(() =>
  import("@/pages/GrantsPage").then((m) => ({ default: m.GrantsPage })),
);
const FunderRecordPage = lazy(() =>
  import("@/pages/FunderRecordPage").then((m) => ({ default: m.FunderRecordPage })),
);
const GrantDetailPage = lazy(() =>
  import("@/pages/GrantDetailPage").then((m) => ({ default: m.default })),
);
const CasesPage = lazy(() =>
  import("@/pages/CasesPage").then((m) => ({ default: m.default })),
);
const CaseDetailPage = lazy(() =>
  import("@/pages/CaseDetailPage").then((m) => ({ default: m.default })),
);
const CaseReferralBriefPage = lazy(() =>
  import("@/pages/CaseReferralBriefPage").then((m) => ({ default: m.default })),
);
const TasksPage = lazy(() =>
  import("@/pages/TasksPage").then((m) => ({ default: m.default })),
);
const ResourceDirectoryPage = lazy(() =>
  import("@/pages/ResourceDirectoryPage").then((m) => ({ default: m.default })),
);
const ResourceOrgDetailPage = lazy(() =>
  import("@/pages/ResourceOrgDetailPage").then((m) => ({ default: m.default })),
);
const ResourceOrgNotesPage = lazy(() =>
  import("@/pages/ResourceOrgNotesPage").then((m) => ({ default: m.default })),
);
const VendorsPage = lazy(() =>
  import("@/pages/VendorsPage").then((m) => ({ default: m.default })),
);
const ProgramsPage = lazy(() =>
  import("@/pages/ProgramsPage").then((m) => ({ default: m.ProgramsPage })),
);
const AssistanceProgramsPage = lazy(() => import("@/pages/AssistanceProgramsPage"));
const CheckInPage = lazy(() => import("@/pages/CheckInPage"));
const WorkSessionPage = lazy(() =>
  import("@/pages/WorkSessionPage").then((m) => ({
    default: m.WorkSessionPage,
  })),
);
const BoardReportPage = lazy(() => import("@/pages/BoardReportPage"));
const PilotAnalyticsPage = lazy(() => import("@/pages/PilotAnalyticsPage"));
const InventoryPage = lazy(() => import("@/pages/InventoryPage"));
const InventoryItemDetailPage = lazy(() => import("@/pages/InventoryItemDetailPage"));
const EventDetailPage = lazy(() => import("@/pages/EventDetailPage"));
const FacilitiesPage = lazy(() =>
  import("@/pages/FacilitiesPage").then((m) => ({ default: m.FacilitiesPage })),
);
const GivingStatementsPage = lazy(() => import("@/pages/GivingStatementsPage"));
const ReportsPage = lazy(() => import("@/pages/ReportsPage"));
const ReferralPortalPage = lazy(() =>
  import("@/pages/ReferralPortalPage").then((m) => ({ default: m.default })),
);
const PlaybookListPage = lazy(() =>
  import("@/pages/PlaybookListPage").then((m) => ({
    default: m.PlaybookListPage,
  })),
);
const PlaybookBuilderPage = lazy(() =>
  import("@/pages/PlaybookBuilderPage").then((m) => ({
    default: m.PlaybookBuilderPage,
  })),
);
const PublicBookingPage = lazy(() =>
  import("@/pages/PublicBookingPage").then((m) => ({
    default: m.PublicBookingPage,
  })),
);
const PublicGivePage = lazy(() =>
  import("@/pages/PublicGivePage").then((m) => ({
    default: m.PublicGivePage,
  })),
);
const DevCasePreviewPage = lazy(() => import("@/pages/DevCasePreviewPage"));
const PublicMeetPage = lazy(() =>
  import("@/pages/PublicMeetPage").then((m) => ({
    default: m.PublicMeetPage,
  })),
);
const VolunteerHoursPage = lazy(() =>
  import("@/pages/VolunteerHoursPage").then((m) => ({
    default: m.VolunteerHoursPage,
  })),
);
const ApplicationsAdminListPage = lazy(() =>
  import("@/pages/ApplicationsAdminListPage").then((m) => ({
    default: m.ApplicationsAdminListPage,
  })),
);
const ApplicationDetailPage = lazy(() =>
  import("@/pages/ApplicationDetailPage").then((m) => ({
    default: m.ApplicationDetailPage,
  })),
);
const WizardPage = lazy(() =>
  import("@/pages/WizardPage").then((m) => ({ default: m.WizardPage })),
);
const ApplicationReceivedPage = lazy(() =>
  import("@/pages/ApplicationReceivedPage").then((m) => ({
    default: m.ApplicationReceivedPage,
  })),
);
const AboutPilotPage = lazy(() =>
  import("@/pages/AboutPilotPage").then((m) => ({ default: m.AboutPilotPage })),
);
const AnnouncementsAdminPage = lazy(() =>
  import("@/pages/AnnouncementsAdminPage").then((m) => ({
    default: m.AnnouncementsAdminPage,
  })),
);
const BillingAdminPage = lazy(() =>
  import("@/pages/BillingAdminPage").then((m) => ({ default: m.BillingAdminPage })),
);
const OrgSettingsPage = lazy(() => import("@/pages/OrgSettingsPage"));

function RequireAuth({ children }: { children: ReactElement }) {
  const isAuthed = useIsAuthenticated();
  const location = useLocation();
  if (!isAuthed) {
    stashReturnPath(`${location.pathname}${location.search}`);
    return <Navigate to="/login" replace />;
  }
  return (
    <>
      <SessionGuard />
      {children}
    </>
  );
}

/**
 * Guard for pre-auth pages (/login, /forgot-password, /reset-password).
 *
 * An already-authenticated user landing on /login is sent home instead.
 * Without this, a logged-in user who deep-links / bookmarks / browser-
 * backs to /login sees the form and could re-submit - silently swapping
 * their session to a different identity or org with no logout step
 * (audit V4). The clean behavior: if you're logged in, /login is just
 * "go home".
 */
function RedirectIfAuthed({ children }: { children: ReactElement }) {
  const isAuthed = useIsAuthenticated();
  const user = useCurrentUser();
  const qaPendingLogin =
    typeof window !== "undefined" &&
    Boolean(sessionStorage.getItem(QA_PENDING_LOGIN_KEY));
  if (isAuthed && !qaPendingLogin) {
    return <Navigate to={homePathForRole(user?.role)} replace />;
  }
  return children;
}

function AccountingFundRedirect() {
  const { id } = useParams<{ id: string }>();
  if (!id) {
    return <Navigate to="/grants/management?tab=funds" replace />;
  }
  return <Navigate to={`/grants/management/funds/${id}`} replace />;
}

function TenantHome() {
  const { isAccountantRole } = useRoleAccess();
  if (isAccountantRole) {
    return <Navigate to="/accounting" replace />;
  }
  return <DashboardPage />;
}

function RequireControl({ children }: { children: ReactElement }) {
  const user = useCurrentUser();
  const { isControl, isLoading } = useDeployment();

  // The access decision is a pure function (see controlAccess.ts) so it
  // can be unit-tested without the router. Mirrors the backend's
  // IsControlUser: control access requires BOTH a control deployment
  // AND a control user.
  const decision = decideControlAccess({
    hasUser: Boolean(user),
    userType: user?.user_type,
    deploymentLoading: isLoading,
    isControlDeployment: isControl,
  });

  if (decision === "login") return <Navigate to="/login" replace />;
  // "wait" renders nothing until the deployment descriptor loads - it
  // must NOT fall through to a role check (audit V3).
  if (decision === "wait") return <PageFallback />;
  if (decision === "deny") return <Navigate to="/" replace />;
  return children;
}

/**
 * Suspense fallback for lazy-loaded pages.
 *
 * Kept deliberately small and neutral: a brief delay between routes
 * shouldn't redraw a full-page skeleton. If the chunk is cached, the
 * fallback is invisible; if it's a cold load, the user sees this for
 * <500ms in normal conditions.
 */
function PageFallback() {
  return (
    <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
      Loading…
    </div>
  );
}

/** The application router: public, auth, and authenticated route trees. */
export function AppRouter() {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        {/* Pre-auth. Wrapped in RedirectIfAuthed so an already-logged-in
            user is sent home rather than shown the login form (V4). */}
        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={
              <RedirectIfAuthed>
                <LoginPage />
              </RedirectIfAuthed>
            }
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
        </Route>

        {/* Public wizard - no auth, no shell. The wizard takes the
            whole viewport for its chat UI. */}
        <Route path="/apply" element={<WizardPage />} />
        <Route path="/apply/received" element={<ApplicationReceivedPage />} />

        {/* Public, token-gated event RSVP - no auth, no shell. The
            backend derives the tenant from the booking token in the URL
            (scheduling Phase 3). */}
        <Route path="/book/:token" element={<PublicBookingPage />} />
        <Route path="/give/:slug" element={<PublicGivePage />} />
        <Route path="/meet/:token" element={<PublicMeetPage />} />
        {/* Local preview of the referred-case panels. Stripped from any
            production build by the DEV guard - the case page itself needs
            auth and a live API, which a stub cannot provide. */}
        {import.meta.env.DEV && (
          <Route path="/dev/case-preview" element={<DevCasePreviewPage />} />
        )}

        {/* Public referral portal - no auth, external caseworkers */}
        <Route path="/portal/referral/:token" element={<ReferralPortalPage />} />

        {/* Public info page - no auth. Pre-application explainer that
            Andrea explicitly asked for during the meeting transcript
            ("we'll have to make sure that people understand what this
            actually is"). Long-form prose covering what the pilot is,
            what's expected, how to apply. */}
        <Route path="/about" element={<AboutPilotPage />} />

        {/* Authenticated app */}
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<TenantHome />} />
          <Route path="/community" element={<ModuleGuard module="people" label="Community"><PermissionGuard permissionModule="members" label="Community"><CommunityPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/donors" element={<ModuleGuard module="people" label="Donors"><PermissionGuard permissionModule="donors" label="Donors"><DonorsPage /></PermissionGuard></ModuleGuard>} />
          {/* A donor's record lives inside Donors rather than in the
              community directory: same person underneath, but the
              questions asked here are fundraising questions. */}
          <Route path="/donors/:kind/:id/notes" element={<ModuleGuard module="people" label="Donor notes"><PermissionGuard permissionModule="donors" label="Donor notes"><DonorNotesPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/donors/:kind/:id" element={<ModuleGuard module="people" label="Donor"><PermissionGuard permissionModule="donors" label="Donor"><DonorRecordPage /></PermissionGuard></ModuleGuard>} />
          {/* The roster segments cover volunteers and staff - this was the
              same list under a second name. */}
          <Route path="/team/directory" element={<Navigate to="/community" replace />} />
          <Route path="/people" element={<Navigate to="/community" replace />} />
          <Route
            path="/people/:id/timeline"
            element={
              <ModuleGuard module="people" label="Work timeline">
                <PermissionGuard permissionModule="members" label="Work timeline">
                  <PermissionGuard permissionModule="volunteer_hours" label="Work timeline">
                    <PersonWorkTimelinePage />
                  </PermissionGuard>
                </PermissionGuard>
              </ModuleGuard>
            }
          />
          <Route path="/people/:id" element={<ModuleGuard module="people" label="Person Profile"><PermissionGuard permissionModule="members" label="Person Profile"><PersonProfilePage /></PermissionGuard></ModuleGuard>} />
          <Route path="/time-tracker" element={<ModuleGuard module="volunteer_hours" label="Time Tracker"><PermissionGuard permissionModule="volunteer_hours" label="Time Tracker"><VolunteerHoursPage /></PermissionGuard></ModuleGuard>} />
          {/* Renamed from Volunteer Hours; keep old links working. */}
          <Route path="/volunteer-hours" element={<Navigate to="/time-tracker" replace />} />
          <Route path="/accounting/funds/:id" element={<AccountingFundRedirect />} />
          <Route path="/accounting" element={<ModuleGuard module="accounting" label="Accounting"><PermissionGuard permissionModule="accounting" label="Accounting"><AccountingPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/grants/management/funds/:id" element={<ModuleGuard module="accounting" label="Fund"><PermissionGuard permissionModule="accounting" label="Fund"><FundDetailPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/grants/management" element={<ModuleGuard module="accounting" label="Grant management"><PermissionGuard permissionModule="accounting" label="Grant management"><GrantManagementPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/scheduling" element={<ModuleGuard module="scheduling" label="Scheduling"><PermissionGuard permissionModule="scheduling" label="Scheduling"><SchedulingPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/facilities" element={<ModuleGuard module="scheduling" label="Facilities"><PermissionGuard permissionModule="facilities" label="Facilities"><FacilitiesPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/submission-center" element={<ModuleGuard module="accounting" label="Submission Center"><PermissionGuard permissionModule="submission_center" anyOfCapabilities={["view", "create", "edit", "delete"]} label="Submission Center"><SubmissionCenterPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/scheduling/events/:id" element={<ModuleGuard module="scheduling" label="Scheduling"><PermissionGuard permissionModule="scheduling" label="Scheduling"><EventDetailPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/events" element={<ModuleGuard module="scheduling" label="Events"><PermissionGuard permissionModule="scheduling" label="Events"><EventsPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/grants" element={<ModuleGuard module="grant_management" label="Grants"><PermissionGuard permissionModule="grants" label="Grants"><GrantsPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/grants/funders/:id" element={<ModuleGuard module="grant_management" label="Funder"><PermissionGuard permissionModule="grants" label="Funder"><FunderRecordPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/grants/:id" element={<ModuleGuard module="grant_management" label="Grant Detail"><PermissionGuard permissionModule="grants" label="Grant Detail"><GrantDetailPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/cases" element={<ModuleGuard module="casework" label="Cases"><PermissionGuard permissionModule="cases" label="Cases"><CasesPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/cases/:id" element={<ModuleGuard module="casework" label="Case Detail"><PermissionGuard permissionModule="cases" label="Case Detail"><CaseDetailPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/cases/:id/referral-brief" element={<ModuleGuard module="casework" label="Referral Brief"><PermissionGuard permissionModule="cases" label="Referral Brief"><CaseReferralBriefPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/tasks" element={<ModuleGuard module="tasks" label="Tasks"><PermissionGuard permissionModule="tasks" label="Tasks"><TasksPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/resource-directory/:id/notes" element={<ModuleGuard module="resource_directory" label="Resource Directory"><PermissionGuard permissionModule="resource_directory" label="Resource notes"><ResourceOrgNotesPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/resource-directory/:id" element={<ModuleGuard module="resource_directory" label="Resource Directory"><PermissionGuard permissionModule="resource_directory" label="Resource Directory"><ResourceOrgDetailPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/resource-directory" element={<ModuleGuard module="resource_directory" label="Resource Directory"><PermissionGuard permissionModule="resource_directory" label="Resource Directory"><ResourceDirectoryPage /></PermissionGuard></ModuleGuard>} />
          {/* Vendors ride the accounting module flag (the viewset requires it)
              but their own permission row, so an ops lead can keep the
              supplier directory without being given the ledger. */}
          <Route path="/vendors" element={<ModuleGuard module="accounting" label="Vendors"><PermissionGuard permissionModule="vendors" label="Vendors"><VendorsPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/suppliers" element={<Navigate to="/vendors" replace />} />
          <Route path="/programs" element={<ModuleGuard module="programs" label="Programs"><PermissionGuard permissionModule="programs" label="Programs"><ProgramsPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/assistance-programs" element={<ModuleGuard module="programs" label="Programs"><PermissionGuard permissionModule="programs" label="Programs"><AssistanceProgramsPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/check-in" element={<ModuleGuard module="checkin" label="Check-In"><PermissionGuard permissionModule="checkin" label="Check-In"><CheckInPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/check-in/people/:personId" element={<ModuleGuard module="checkin" label="Work session"><PermissionGuard permissionModule="checkin" label="Work session"><WorkSessionPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/inventory" element={<ModuleGuard module="inventory" label="Inventory"><PermissionGuard permissionModule="inventory" label="Inventory"><InventoryPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/inventory/items/:id" element={<ModuleGuard module="inventory" label="Inventory"><PermissionGuard permissionModule="inventory" label="Inventory"><InventoryItemDetailPage /></PermissionGuard></ModuleGuard>} />
          <Route
            path="/copilot/playbooks"
            element={<ModuleGuard module="ai_copilot" label="Copilot Workflows"><PermissionGuard permissionModule="copilot" label="Copilot Workflows"><PlaybookListPage /></PermissionGuard></ModuleGuard>}
          />
          <Route
            path="/copilot/playbooks/:id"
            element={<ModuleGuard module="ai_copilot" label="Playbook Editor"><PermissionGuard permissionModule="copilot" capability="edit" label="Playbook Editor"><PlaybookBuilderPage /></PermissionGuard></ModuleGuard>}
          />
          <Route path="/team" element={<Navigate to="/community" replace />} />
          <Route path="/team/invitations" element={<PermissionGuard permissionModule="invitations" label="Team Invitations"><TeamInvitationsPage /></PermissionGuard>} />
          <Route path="/giving-statements" element={<ModuleGuard module="accounting" label="Giving Statements"><PermissionGuard permissionModule="accounting" label="Giving Statements"><GivingStatementsPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/reports" element={<ModuleGuard module="accounting" label="Reports"><PermissionGuard permissionModule="reports" label="Reports"><ReportsPage /></PermissionGuard></ModuleGuard>} />
          <Route path="/board-report" element={<RoleGuard allowedRoles={["OWNER","ADMIN","BOARD"]} label="Board Report"><BoardReportPage /></RoleGuard>} />
          <Route path="/settings" element={<SettingsPageGuard><OrgSettingsPage /></SettingsPageGuard>} />
          <Route
            path="/settings/team-permissions"
            element={<Navigate to="/settings?section=permissions" replace />}
          />
          <Route
            path="/data"
            element={
              <PermissionGuard permissionModule="data" label="Data Management">
                <Navigate to="/settings?section=data" replace />
              </PermissionGuard>
            }
          />
          <Route
            path="/activity"
            element={
              <PermissionGuard permissionModule="settings" label="Recent Activity">
                <Navigate to="/settings?section=activity" replace />
              </PermissionGuard>
            }
          />
          <Route
            path="/control"
            element={
              <RequireControl>
                <ControlPlanePage />
              </RequireControl>
            }
          />
          <Route
            path="/control/kpis"
            element={
              <RequireControl>
                <KPIDashboardPage />
              </RequireControl>
            }
          />
          <Route
            path="/control/analytics"
            element={
              <RequireControl>
                <PilotAnalyticsPage />
              </RequireControl>
            }
          />
          <Route
            path="/control/applications"
            element={
              <RequireControl>
                <ApplicationsAdminListPage />
              </RequireControl>
            }
          />
          <Route
            path="/control/applications/:id"
            element={
              <RequireControl>
                <ApplicationDetailPage />
              </RequireControl>
            }
          />
          <Route
            path="/control/announcements"
            element={
              <RequireControl>
                <AnnouncementsAdminPage />
              </RequireControl>
            }
          />
          <Route
            path="/control/billing"
            element={
              <RequireControl>
                <BillingAdminPage />
              </RequireControl>
            }
          />
          <Route
            path="/billing"
            element={
              <PermissionGuard permissionModule="billing" label="Billing">
                <Navigate to="/settings?section=billing" replace />
              </PermissionGuard>
            }
          />
        </Route>

        {/* Catch-all - a real "not found" page (audit fix UX3) rather
            than a silent redirect that could confuse a user who followed
            a stale link. */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

/**
 * 404 page for unknown routes. Audit fix UX3 - replaces a silent
 * Navigate-to-dashboard so a user who followed a bad/stale link is told
 * the page does not exist instead of being quietly moved elsewhere.
 */
function NotFound() {
  return (
    <Result
      status="404"
      title="Page not found"
      subTitle="The page you're looking for doesn't exist or may have moved."
      extra={
        <Link to="/">
          <Button type="primary">Back to dashboard</Button>
        </Link>
      }
    />
  );
}
